import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { 
    Bell, AlertTriangle, AlertCircle, Info, CheckCircle, Shield, Activity, Calendar
} from 'lucide-react';

const Alerts = () => {
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [stats, setStats] = useState({ warning: 0, critical: 0, info: 0 });
    const [isLoading, setIsLoading] = useState(true);

    // 1. Фильтр по дате (по умолчанию - текущий день)
    const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
    
    // 2. Пагинация (Lazy Load)
    const [visibleCount, setVisibleCount] = useState(10);

    // Сброс пагинации при смене даты
    useEffect(() => {
        setVisibleCount(10);
    }, [filterDate]);

    // Загрузка данных
    useEffect(() => {
        fetchAlerts();
    }, []);

    const fetchAlerts = async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/alerts');
            setLogs(res.data.logs || []);
            setStats(res.data.stats || { warning: 0, critical: 0, info: 0 });
        } catch (error) {
            console.error("Ошибка загрузки алертов:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Применение фильтра по дате
    useEffect(() => {
        let result = logs;
        if (filterDate) {
            result = result.filter(log => log.created_at && log.created_at.startsWith(filterDate));
        }
        setFilteredLogs(result);
    }, [filterDate, logs]);

    // Бесконечный скролл (Infinite Scroll)
    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 200) {
                setVisibleCount(prev => prev + 10);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Функция для красивого отображения времени
    const timeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);

        let interval = seconds / 31536000;
        if (interval > 1) return Math.floor(interval) + " лет назад";
        interval = seconds / 2592000;
        if (interval > 1) return Math.floor(interval) + " мес. назад";
        interval = seconds / 86400;
        if (interval > 1) return Math.floor(interval) + " дн. назад";
        interval = seconds / 3600;
        if (interval > 1) return Math.floor(interval) + " ч. назад";
        interval = seconds / 60;
        if (interval > 1) return Math.floor(interval) + " мин. назад";
        return "Только что";
    };

    const formatTime = (dateString) => {
        const d = new Date(dateString);
        return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    };

    const getVariant = (level) => {
        switch(level) {
            case 'error': return { style: 'variant-red', icon: <AlertCircle size={20} className="text-red"/>, badge: 'badge-gray' };
            case 'warning': return { style: 'variant-yellow', icon: <AlertTriangle size={20} className="text-yellow-dark"/>, badge: 'badge-gray' };
            case 'success': return { style: 'variant-green', icon: <CheckCircle size={20} className="text-green"/>, badge: 'badge-primary' };
            default: return { style: 'variant-blue', icon: <Info size={20} className="text-blue"/>, badge: 'badge-default' };
        }
    };

    // Отрезаем список для Lazy Load
    const visibleLogs = filteredLogs.slice(0, visibleCount);

    return (
        <div>
            {/* 1. ЗАГОЛОВОК */}
            <div className="card alert-header-card" style={{ marginBottom: '16px' }}>
                <div className="card-header alert-header-row">
                    <h4 className="card-title text-xl" style={{display:'flex', alignItems:'center', gap:'10px'}}>
                        <Bell className="text-red" size={24} />
                        Уведомления и алерты
                    </h4>
                    <p className="card-subtitle mt-1">Мониторинг активности и безопасности системы</p>
                </div>
            </div>

            {/* 2. МОНИТОРИНГ БЕЗОПАСНОСТИ (ПОДНЯТ НАВЕРХ) */}
            <div className="card border-purple" style={{ marginBottom: '24px', gap: '0' }}>
                <div className="card-header alert-header-row">
                    <h4 className="card-title" style={{display:'flex', alignItems:'center', gap:'10px'}}>
                        <Shield className="text-purple" size={20} />
                        Мониторинг безопасности
                    </h4>
                </div>
                <div className="card-body">
                    <div className="monitoring-grid">
                        <div className="monitoring-box variant-green">
                            <div className="monitoring-header">
                                <CheckCircle size={20} className="text-green"/>
                                <p className="monitoring-title text-green-dark">Нет подозрительной активности</p>
                            </div>
                            <p className="monitoring-desc text-green-dark">Все действия пользователей в пределах нормы</p>
                        </div>
                        <div className="monitoring-box variant-blue">
                            <div className="monitoring-header">
                                <Activity size={20} className="text-blue"/>
                                <p className="monitoring-title text-blue-dark">Мониторинг активен</p>
                            </div>
                            <p className="monitoring-desc text-blue-dark">Система отслеживает все операции в реальном времени</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* 3. СЕТКА СТАТИСТИКИ (За все время) */}
            <div className="alerts-stat-grid" style={{ marginBottom: '24px' }}>
                <div className="card stat-card variant-yellow">
                    <div className="card-body stat-card-body">
                        <div className="stat-flex">
                            <div>
                                <p className="stat-label">Предупреждения</p>
                                <p className="stat-number text-yellow-dark">{stats.warning}</p>
                            </div>
                            <AlertTriangle size={32} className="text-yellow-dark"/>
                        </div>
                    </div>
                </div>
                <div className="card stat-card variant-red">
                    <div className="card-body stat-card-body">
                        <div className="stat-flex">
                            <div>
                                <p className="stat-label">Критичные</p>
                                <p className="stat-number text-red">{stats.critical}</p>
                            </div>
                            <AlertCircle size={32} className="text-red"/>
                        </div>
                    </div>
                </div>
                <div className="card stat-card variant-blue">
                    <div className="card-body stat-card-body">
                        <div className="stat-flex">
                            <div>
                                <p className="stat-label">Информация</p>
                                <p className="stat-number text-blue">{stats.info}</p>
                            </div>
                            <Info size={32} className="text-blue"/>
                        </div>
                    </div>
                </div>
                <div className="card stat-card variant-green">
                    <div className="card-body stat-card-body">
                        <div className="stat-flex">
                            <div>
                                <p className="stat-label">Система</p>
                                <p className="stat-number text-green">{stats.critical === 0 ? "OK" : "WARN"}</p>
                            </div>
                            <CheckCircle size={32} className="text-green"/>
                        </div>
                    </div>
                </div>
            </div>

            {/* 4. ЗАГОЛОВОК ЖУРНАЛА И ФИЛЬТР ПО ДАТЕ */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#111827', margin: 0 }}>Журнал событий</h3>
                
                {/* Фильтр Даты */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'white', padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                    <Calendar size={18} color="#6b7280" />
                    <input 
                        type="date" 
                        style={{ border: 'none', background: 'transparent', outline: 'none', color: '#374151', fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'inherit' }}
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                        title="Выберите дату для фильтрации"
                    />
                    {filterDate && (
                        <button onClick={() => setFilterDate('')} style={{background: 'none', border: 'none', color: '#ef4444', fontSize: '0.8rem', cursor: 'pointer', padding: '0 0 0 8px', marginLeft: '8px', borderLeft: '1px solid #e5e7eb'}}>
                            Сбросить
                        </button>
                    )}
                </div>
            </div>

            {/* 5. СПИСОК АЛЕРТОВ */}
            <div className="alerts-list-group">
                {isLoading ? (
                    <p style={{textAlign:'center', color:'#888', padding: '20px'}}>Загрузка событий...</p>
                ) : visibleLogs.length === 0 ? (
                    <div className="card alert-item variant-green">
                        <div className="card-body alert-item-body">
                            <div className="alert-content-flex">
                                <div className="alert-icon mt-05">
                                    <CheckCircle size={20} className="text-green"/>
                                </div>
                                <div className="alert-text-block">
                                    <div className="alert-header">
                                        <h3 className="alert-title">Журнал чист</h3>
                                        <span className="badge badge-primary">Система</span>
                                    </div>
                                    <p className="alert-desc">За выбранную дату событий не зафиксировано.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    visibleLogs.map(log => {
                        const variant = getVariant(log.level);
                        return (
                            <div key={log.id} className={`card alert-item ${variant.style}`}>
                                <div className="card-body alert-item-body">
                                    <div className="alert-content-flex">
                                        <div className="alert-icon mt-05">
                                            {variant.icon}
                                        </div>
                                        <div className="alert-text-block">
                                            <div className="alert-header">
                                                <h3 className="alert-title">{log.category || 'Уведомление'}</h3>
                                                <span className={`badge ${variant.badge}`}>{log.level.toUpperCase()}</span>
                                            </div>
                                            <p className="alert-desc">{log.message}</p>
                                            <p className="alert-time">
                                                {formatTime(log.created_at)} • {timeAgo(log.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* ИНДИКАТОР ПРОКРУТКИ */}
            {!isLoading && visibleLogs.length < filteredLogs.length && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    Прокрутите вниз, чтобы загрузить еще...
                </div>
            )}
        </div>
    );
};

export default Alerts;